import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Package, Mail, MailOpen, Plus, ArrowRight } from "lucide-react";

const CATEGORY_LABELS = {
  sneakers: "Sneakers",
  wigs: "Suzel Luxury Hair",
  couture: "Habillement",
  epicerie: "Travel & Market",
  patisserie: "Suzel Bakery",
  printing: "Printing & IT",
};

const AdminDashboard = () => {
  const { authClient } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    authClient()
      .get("/api/admin/stats")
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, [authClient]);

  return (
    <div data-testid="admin-dashboard" className="max-w-6xl">
      <div className="flex items-center justify-between mb-12">
        <div>
          <p className="overline mb-2">Tableau de bord</p>
          <h1 className="font-display text-4xl text-white">
            Bonjour <span className="italic text-[#C5A059]">Admin</span>
          </h1>
        </div>
        <Link to="/admin/products/new" data-testid="dashboard-new-product" className="btn-gold">
          <Plus size={14} /> Nouveau produit
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
        <StatCard icon={<Package />} label="Produits au total" value={stats?.total_products ?? "—"} testid="stat-total-products" />
        <StatCard icon={<Mail />} label="Messages reçus" value={stats?.total_messages ?? "—"} testid="stat-total-messages" />
        <StatCard
          icon={<MailOpen />}
          label="Messages non lus"
          value={stats?.unread_messages ?? "—"}
          accent={(stats?.unread_messages ?? 0) > 0}
          testid="stat-unread-messages"
        />
      </div>

      {/* Categories breakdown */}
      <div className="bg-[#0a0a0a] border border-white/5 p-8">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-display text-2xl text-white">Produits par division</h2>
          <Link to="/admin/products" className="text-xs text-[#C5A059] uppercase tracking-[0.2em] font-grotesk link-underline">
            Gérer les produits →
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
            <Link
              to={`/admin/products?category=${key}`}
              key={key}
              data-testid={`stat-cat-${key}`}
              className="bg-[#121212] border border-white/5 hover:border-[#C5A059]/30 p-5 transition-colors group"
            >
              <p className="overline text-[10px] mb-3">{label}</p>
              <p className="font-display text-3xl text-white group-hover:text-[#C5A059] transition-colors">
                {stats?.products_by_category?.[key] ?? "—"}
              </p>
            </Link>
          ))}
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
        <QuickLink to="/admin/products" label="Gérer les produits" testid="quick-products" />
        <QuickLink to="/admin/messages" label="Voir les messages" testid="quick-messages" />
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value, accent = false, testid }) => (
  <div
    data-testid={testid}
    className={`bg-[#0a0a0a] border p-6 ${accent ? "border-[#C5A059]/40" : "border-white/5"}`}
  >
    <div className="flex items-start justify-between mb-4">
      <p className="overline">{label}</p>
      <span className={`${accent ? "text-[#C5A059]" : "text-white/30"}`}>{icon}</span>
    </div>
    <p className="font-display text-4xl text-white">{value}</p>
  </div>
);

const QuickLink = ({ to, label, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="flex items-center justify-between bg-[#0a0a0a] border border-white/5 hover:border-[#C5A059]/30 p-6 transition-colors group"
  >
    <span className="text-white font-grotesk text-sm uppercase tracking-[0.18em]">{label}</span>
    <ArrowRight size={16} className="text-white/40 group-hover:text-[#C5A059] group-hover:translate-x-1 transition-all" />
  </Link>
);

export default AdminDashboard;
